/**
 * Entry route B (f11.4): the Commitments tab on Client detail, after the
 * prototype's `client-detail.html` `#panel-commitments` ("Commitment by cycle").
 *
 * One row per cycle the server lists for this viewer, newest first. Each opens
 * f11.1's `ForecastGridPage` -- the SAME route and component the cycle rail
 * (route A) opens -- with the client preselected. This panel imports no grid
 * module and decides nothing: scope, grants, cycle state and the time window
 * are the server's (BR-F11.4-1 .. BR-F11.4-5).
 *
 * Deliberately absent from the prototype row: `Delta to last COM` (review
 * scope, f12.x) and `Risk` (no risk source exists) -- Q-f11.4-2.
 */

import { AlertTriangle, ArrowRight, RotateCw, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { Client, ClientCycleCommitment, CommitmentCurrencyFigures } from '@/api/client';
import { useClientCommitments } from '@/api/queries';
import { CycleStateBadge } from '@/routes/cycles/CycleStateBadge';
import { detailFailureOf } from '@/ui/RecordUnavailable';

import { formatMoney, forecastGridHref, openWindowHint, statusAmount } from './commitmentsView';

type Figures = readonly CommitmentCurrencyFigures[];

/** One line per currency, each labelled with its code; never converted, never summed across. */
function MoneyCell({ figures, pick, strong = false }: {
  figures: Figures;
  pick: (f: CommitmentCurrencyFigures) => string | null;
  strong?: boolean;
}) {
  const labelled = figures.length > 1;
  return (
    <td className={strong ? 'num commitment-committed' : 'num'}>
      <div className="commitment-stack">
        {figures.map((f) => (
          <span key={f.currency_code}>
            {formatMoney(pick(f) ?? '0', f.currency_code)}
            {labelled && <span className="hint mono commitment-code">{f.currency_code}</span>}
          </span>
        ))}
      </div>
    </td>
  );
}

function InputCell({ item }: { item: ClientCycleCommitment }) {
  if (!item.pending_input) return <td><span className="hint">Read only</span></td>;
  const hint = openWindowHint(item);
  return (
    <td>
      <div className="commitment-stack commitment-stack-start">
        <span className="badge pill-warning">Pending input</span>
        {hint && <span className="hint">{hint}</span>}
      </div>
    </td>
  );
}

function OpenCell({ item, clientId }: { item: ClientCycleCommitment; clientId: string }) {
  const units = item.business_units;
  if (units.length === 1 && units[0]) {
    return (
      <td>
        <Link className="btn btn-ghost btn-sm" to={forecastGridHref(item.cycle.id, units[0].id, clientId)}>
          Open forecast grid <ArrowRight aria-hidden="true" />
        </Link>
      </td>
    );
  }
  return (
    <td>
      <div className="commitment-stack commitment-stack-start">
        {units.map((unit) => (
          <Link key={unit.id} className="btn btn-ghost btn-sm" to={forecastGridHref(item.cycle.id, unit.id, clientId)}>
            {unit.name} <ArrowRight aria-hidden="true" />
          </Link>
        ))}
      </div>
    </td>
  );
}

function PanelState({ title, body, onRetry }: { title: string; body: string; onRetry?: () => void }) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="empty">
          <div className="e-icon e-icon-warning"><AlertTriangle aria-hidden="true" /></div>
          <h3>{title}</h3>
          <p>{body}</p>
          {onRetry && (
            <p className="empty-actions">
              <button type="button" className="btn btn-secondary" onClick={onRetry}>
                <RotateCw aria-hidden="true" /> Retry
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function CommitmentsPanel({ client }: { client: Client }) {
  const commitments = useClientCommitments(client.id);

  if (commitments.isPending) {
    return (
      <div className="card" aria-busy="true" aria-label="Loading the Commitments">
        <div className="card-body">
          {[0, 1, 2].map((index) => (
            <span key={index} className="skeleton-bar group-skeleton-line" />
          ))}
        </div>
      </div>
    );
  }

  if (commitments.error || !commitments.data) {
    const kind = detailFailureOf(commitments.error);
    if (kind === 'unavailable') {
      return <PanelState title="This client's commitments cannot be opened" body="The client is not available to your account." />;
    }
    if (kind === 'refused') {
      return <PanelState title="Not permitted" body="The server refuses this for your account, and records the refusal in the audit log." />;
    }
    return (
      <PanelState
        title="The Commitments could not be loaded"
        body={commitments.error instanceof Error ? commitments.error.message : 'Unexpected error.'}
        onRetry={() => void commitments.refetch()}
      />
    );
  }

  const { cycles, lookups } = commitments.data;
  const statuses = lookups.statuses;

  return (
    <div className="card">
      <div className="card-head">
        <h3>Commitment by cycle</h3>
      </div>
      {cycles.length === 0 ? (
        <div className="card-body">
          <div className="empty">
            <div className="e-icon"><TrendingUp aria-hidden="true" /></div>
            <h3>No forecast cycle includes {client.name} yet</h3>
            <p>Cycles appear here once a released cycle carries forecast lines of this client that you may see.</p>
          </div>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="data zebra">
            <thead>
              <tr>
                <th>Cycle</th>
                <th>Label</th>
                <th>State</th>
                <th>Input</th>
                {statuses.map((status) => (
                  <th key={status.code} className="num">{status.name}</th>
                ))}
                <th className="num">Committed</th>
                <th className="num">Unweighted</th>
                <th><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {cycles.map((item) => (
                <tr key={item.cycle.id}>
                  <td className="mono">{item.cycle.code}</td>
                  <td>
                    {item.cycle.label}
                    {item.results_pending && <div className="hint">Recalculating…</div>}
                  </td>
                  <td><CycleStateBadge state={item.cycle.state} /></td>
                  <InputCell item={item} />
                  {statuses.map((status) => (
                    <MoneyCell key={status.code} figures={item.figures} pick={(f) => statusAmount(f, status.code)} />
                  ))}
                  <MoneyCell figures={item.figures} pick={(f) => f.committed.amount} strong />
                  <MoneyCell figures={item.figures} pick={(f) => f.unweighted_revenue.amount} />
                  <OpenCell item={item} clientId={client.id} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
